/**
 * Bluebird primitives — the handful of shapes the comp repeats.
 *
 * Everything here is a port, not an interpretation. Values come from
 * `@/lib/design-tokens`, which was measured off the rendered comp by
 * `script/extract_tokens.mjs`. Styles stay inline for the same reason the comp
 * keeps them inline: the diff harness compares computed geometry section by
 * section, and a value that lives in one place is a value that can be checked
 * against the comp in one place.
 */

import type { CSSProperties, ReactNode } from "react";
import type { LinkComponentBaseProps } from "@inertiajs/core";
import { Link, type InertiaLinkProps } from "@inertiajs/react";
import { color, font, maxWidth, radius, space } from "@/lib/design-tokens";

type LinkLikeProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof LinkComponentBaseProps | "style" | "onClick"
> & {
  onClick?: InertiaLinkProps["onClick"];
};

/**
 * The 1200px content column with the fluid page gutter OUTSIDE it.
 *
 * The gutter being outside is not incidental — the comp renders content-box, so
 * a `Wrap` at 1440px measures 1296px wide (1200 + 48 + 48). `.bluebird` in
 * app/frontend/styles/bluebird.css restores content-box for exactly this
 * reason; under Tailwind's border-box the same markup would render a 1104px
 * content column and every section below would shift.
 */
export function Wrap({
  children,
  style,
  padY,
  ...rest
}: {
  children: ReactNode;
  style?: CSSProperties;
  padY?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "style">) {
  return (
    <div
      style={{
        maxWidth,
        margin: "0 auto",
        padding: padY ? `${padY} ${space.gutter}` : `0 ${space.gutter}`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * A photography slot.
 *
 * Real product photography is being sourced in a parallel session; until it
 * lands, every image slot renders the comp's own placeholder treatment — a
 * diagonal hatch plus a mono label naming the shot that belongs there. That is
 * what the comp ships, so the slots are pixel-comparable today, and each one
 * becomes an `<img>` the day the files arrive. The AI frames in
 * `docs/design/placeholder-photography/` are atmosphere-only and do not ship.
 */
export function PhotoSlot({
  label,
  tone = "light",
  style,
  labelSize = 12,
  pad = 16,
  hatchStep = 14,
  children,
  ...rest
}: {
  label: string;
  /** `blue` for a slot sitting on the blue band, `light` for one on white. */
  tone?: "light" | "blue";
  style?: CSSProperties;
  labelSize?: number;
  pad?: number;
  /** Stripe width. The comp uses 14px on large slots and 12px on small ones. */
  hatchStep?: number;
  children?: ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "style" | "children">) {
  const blue = tone === "blue";

  // When a real photograph is passed in, the slot becomes its frame: the hatch
  // and the mono caption are the PLACEHOLDER, describing a picture that does
  // not exist yet. Leaving the caption over a photo we already have is the
  // exact defect the homepage bike grid shipped with — a label reading
  // "photo — side profile" printed across an actual side profile.
  const filled = Boolean(children);

  return (
    <div
      {...rest}
      style={{
        background: filled
          ? undefined
          : blue
          ? `repeating-linear-gradient(45deg,${color.hatchBlueLight},${color.hatchBlueLight} ${hatchStep}px,${color.hatchBlueDark} ${hatchStep}px,${color.hatchBlueDark} ${hatchStep * 2}px)`
          : `repeating-linear-gradient(45deg,${color.surface},${color.surface} ${hatchStep}px,${color.hatchLight} ${hatchStep}px,${color.hatchLight} ${hatchStep * 2}px)`,
        display: "flex",
        alignItems: "flex-end",
        padding: `${pad}px`,
        // Anchors an absolutely-positioned child photograph. Harmless when
        // empty — the placeholder is in normal flow either way, so the comp's
        // geometry is unchanged and script/design_diff.mjs still measures 0%.
        position: "relative",
        overflow: filled ? "hidden" : undefined,
        ...style,
      }}
    >
      {!filled && (
        <span
          style={{
            fontFamily: font.mono,
            fontSize: `${labelSize}px`,
            color: blue ? color.onBlue : color.slate,
            background: blue ? "rgba(0,40,104,.85)" : "rgba(255,255,255,.8)",
            padding: labelSize <= 11 ? "3px 7px" : "5px 9px",
            borderRadius: radius.chip,
          }}
        >
          {label}
        </span>
      )}
      {children}
    </div>
  );
}

type ButtonVariant =
  | "red"
  | "gold"
  | "goldSoft"
  | "blue"
  | "blueFlat"
  | "ghost"
  | "ghostSoft"
  | "outlineBlue";

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  red: "bb-btn bb-btn-red",
  gold: "bb-btn bb-btn-gold",
  goldSoft: "bb-btn bb-btn-gold-soft",
  blue: "bb-btn bb-btn-blue",
  blueFlat: "bb-btn bb-btn-blue-flat",
  ghost: "bb-btn bb-btn-ghost",
  ghostSoft: "bb-btn bb-btn-ghost-soft",
  outlineBlue: "bb-btn bb-btn-outline-blue",
};

/**
 * The comp's button.
 *
 * Hover and press states live in bluebird.css because the comp expresses them
 * as `style-hover` / `style-active` attributes resolved by its own generated
 * runtime, and that runtime is deliberately not ported. Static geometry stays
 * inline so it can be diffed.
 *
 * Renders an Inertia `<Link>` for an internal route and a plain `<a>` for
 * `tel:`, `sms:`, `mailto:` and hash targets — phone protocols and hash targets
 * must stay native or Inertia will turn them into broken Rails paths.
 */
export function Button({
  href,
  variant,
  children,
  style,
  ...rest
}: {
  href: string;
  variant: ButtonVariant;
  children: ReactNode;
  style?: CSSProperties;
} & LinkLikeProps) {
  const className = VARIANT_CLASS[variant];
  const external =
    href.startsWith("tel:") ||
    href.startsWith("sms:") ||
    href.startsWith("mailto:") ||
    href.startsWith("#") ||
    href.startsWith("http");

  if (external) {
    return (
      <a href={href} className={className} style={style} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={className} style={style} {...rest}>
      {children}
    </Link>
  );
}

/** A link that is a link, routed the same way `Button` routes. */
export function A({
  href,
  children,
  className,
  style,
  ...rest
}: {
  href: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
} & Omit<LinkLikeProps, "className">) {
  const external =
    href.startsWith("tel:") ||
    href.startsWith("sms:") ||
    href.startsWith("mailto:") ||
    href.startsWith("#") ||
    href.startsWith("http");
  if (external) {
    return (
      <a href={href} className={className} style={style} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={className} style={style} {...rest}>
      {children}
    </Link>
  );
}

/** Section heading — one size, used on every page. */
export function H2({
  children,
  style,
  ...rest
}: {
  children: ReactNode;
  style?: CSSProperties;
} & Omit<React.HTMLAttributes<HTMLHeadingElement>, "style">) {
  return (
    <h2
      style={{
        margin: 0,
        fontWeight: 800,
        fontSize: "clamp(28px,3.6vw,40px)",
        letterSpacing: "-.01em",
        ...style,
      }}
      {...rest}
    >
      {children}
    </h2>
  );
}
